import { toast } from 'sonner'
import { Link } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Download,
  CheckCircle2,
  CalendarArrowUp,
  AlertTriangle,
  Truck,
  MapPin,
  Calendar,
  Package,
  ArrowRight,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
} from 'lucide-react'
import { useRetryShipmentBooking, useBookShipment, useCorrectShipmentStatus } from '@/api/hooks'
import { isShipmentToBook } from './shipment-booking'
import { parseTrackingNumbers } from './tracking-number-links'
import { ShipmentBookerPicker, ShipmentBookingChecklist, ShipmentModePicker } from './shipment-controls'
import { AddShipmentPanel } from './add-shipment-panel'
import { useAuth } from '@/hooks/use-auth'
import { ShipmentDateCard } from '@/components/shipment-dates'
import { ShipmentHistory } from './shipment-history'
import { ShipmentRevisions } from './shipment-revisions'
import { HandBookingForm } from '@/components/hand-booking-form'
import { ProductSummaryRow } from '@/components/product-summary-list'
import { ShipmentReferenceFields, ShipmentStatusEdit } from '@/components/shipment-status-edit'
import { formatApiErrorMessage } from '@/lib/api-error'
import { cn, formatDateOnly, formatMoney, parseUTCDate } from '@/lib/utils'
import { useState, type Dispatch, type SetStateAction } from 'react'
import type { PurchaseOrderDetail } from './types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

function formatTime12h(time: string): string {
  const [hStr, mStr] = time.split(':')
  if (!hStr || !mStr) return time
  let h = parseInt(hStr, 10)
  const m = mStr.padStart(2, '0')
  const suffix = h >= 12 ? 'pm' : 'am'
  if (h === 0) h = 12
  else if (h > 12) h -= 12
  return `${h}:${m}${suffix}`
}

type Shipment = PurchaseOrderDetail['shipments'][number]

function shortDate(date: string) {
  return formatDateOnly(date).replace(/\/(\d{4})$/, (_, year: string) =>
    year === String(new Date().getFullYear()) ? '' : `/${year.slice(2)}`,
  )
}

/** The facts staff check most, on one line; the full shipment opens on click. */
function ShipmentSummary({ shipment, onExpand }: { shipment: Shipment; onExpand: () => void }) {
  const facts: Array<{ label: string; value: string; mono?: boolean }> = []
  if (shipment.actual_ship_date) {
    facts.push({ label: 'Shipped', value: shortDate(shipment.actual_ship_date) })
  } else if (shipment.estimated_ship_date) {
    facts.push({ label: 'Est. ship', value: shortDate(shipment.estimated_ship_date) })
  }
  if (shipment.estimated_delivery_date) {
    facts.push({ label: 'Est. delivery', value: shortDate(shipment.estimated_delivery_date) })
  }
  if (shipment.pickup_date && !shipment.actual_ship_date) {
    const window =
      shipment.pickup_time_start || shipment.pickup_time_end
        ? ` ${formatTime12h(shipment.pickup_time_start || '?')}–${formatTime12h(shipment.pickup_time_end || '?')}`
        : ''
    facts.push({ label: 'Pickup', value: `${shortDate(shipment.pickup_date)}${window}` })
  }
  if (shipment.carrier_name) facts.push({ label: 'Carrier', value: shipment.carrier_name })
  if (shipment.pro_number) facts.push({ label: 'PRO', value: shipment.pro_number, mono: true })
  else if (shipment.tracking_numbers) {
    // Stored as a JSON list; staff read the numbers (USA-1554).
    facts.push({ label: 'Tracking', value: parseTrackingNumbers(shipment.tracking_numbers).join(', '), mono: true })
  }
  const load = [
    shipment.pallet_count &&
      `${shipment.pallet_count} pallet${shipment.pallet_count === 1 ? '' : 's'}`,
    shipment.total_weight_lbs && `${shipment.total_weight_lbs} lbs`,
  ].filter(Boolean)
  if (load.length) facts.push({ label: 'Load', value: load.join(' · ') })
  if (shipment.shipping_cost != null) {
    facts.push({ label: 'Cost', value: formatMoney(shipment.shipping_cost) })
  }

  return (
    <button
      type="button"
      onClick={onExpand}
      aria-expanded={false}
      aria-label="Show shipment details"
      className="group flex w-full items-start gap-3 rounded-md bg-muted/40 px-3 py-2 text-left transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      <span className="flex min-w-0 flex-1 flex-wrap gap-x-5 gap-y-1.5 text-sm">
        {facts.length === 0 && <span className="text-muted-foreground">No dates or carrier yet</span>}
        {facts.map((fact) => (
          <span key={fact.label} className="block min-w-0">
            <span className="block text-[11px] text-muted-foreground">{fact.label}</span>
            <span
              className={cn('block font-medium tabular-nums', fact.mono && 'font-mono text-[13px]')}
            >
              {fact.value}
            </span>
          </span>
        ))}
      </span>
      <span className="flex shrink-0 items-center gap-1 self-center text-xs text-muted-foreground group-hover:text-foreground">
        Details
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      </span>
    </button>
  )
}

export function POShippingSection({
  po,
  showCancelledShipments,
  setShowCancelledShipments,
  showAddShipment,
  setShowAddShipment,
  setEditShipmentId,
  setRebookShipmentId,
  correctShipmentStatus,
  bookShipmentMutation,
  retryBookingMutation,
  getAccessToken,
}: {
  po: PurchaseOrderDetail
  showCancelledShipments: boolean
  setShowCancelledShipments: Dispatch<SetStateAction<boolean>>
  showAddShipment: boolean
  setShowAddShipment: Dispatch<SetStateAction<boolean>>
  setEditShipmentId: Dispatch<SetStateAction<number | null>>
  setRebookShipmentId: Dispatch<SetStateAction<number | null>>
  correctShipmentStatus: ReturnType<typeof useCorrectShipmentStatus>
  bookShipmentMutation: ReturnType<typeof useBookShipment>
  retryBookingMutation: ReturnType<typeof useRetryShipmentBooking>
  getAccessToken: ReturnType<typeof useAuth>['getAccessToken']
}) {
  // Shipments start as a one-line summary; details open per shipment.
  const [openShipments, setOpenShipments] = useState<Set<number>>(() => new Set())
  const [handBookingShipmentId, setHandBookingShipmentId] = useState<number | null>(null)
  const toggleShipment = (id: number) =>
    setOpenShipments((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  return (
    <>
      {/* Shipping */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {(() => {
            const total = po.shipments?.length ?? 0
            if (total === 0) return 'Shipping'
            const cancelled = (po.shipments ?? []).filter((s) => s.status === 'cancelled').length
            const active = total - cancelled
            if (cancelled === 0) return `Shipping (${total})`
            return `Shipping (${active} active, ${cancelled} cancelled)`
          })()}
        </h3>
        {!showAddShipment && (
          <Button variant="outline" size="sm" onClick={() => setShowAddShipment(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add shipment
          </Button>
        )}
      </div>
      {showAddShipment && <AddShipmentPanel po={po} onDone={() => setShowAddShipment(false)} />}
      {(po.shipments?.length ?? 0) === 0 && !showAddShipment && (
        <Card>
          <CardContent className="px-4 py-6 text-sm text-muted-foreground">
            No shipments yet.
          </CardContent>
        </Card>
      )}
      {po.shipments?.length
        ? (po.shipments ?? [])
            .filter((s) => s.status !== 'cancelled' || showCancelledShipments)
            // Active first so a cancelled-then-rebooked PO still shows the
            // live booking at the top once cancelled are toggled visible.
            .sort((a, b) => {
              const aCancelled = a.status === 'cancelled' ? 1 : 0
              const bCancelled = b.status === 'cancelled' ? 1 : 0
              return aCancelled - bCancelled
            })
            .map((shipment) => {
              const pickupAccessorials = [
                shipment.inside_pickup && 'Inside Pickup',
                shipment.liftgate_pickup && 'Liftgate Pickup',
                shipment.residential_pickup && 'Residential Pickup',
                shipment.tradeshow_pickup && 'Tradeshow Pickup',
                shipment.construction_site_pickup && 'Construction Site Pickup',
              ].filter(Boolean) as string[]
              const deliveryAccessorials = [
                shipment.liftgate_required && 'Liftgate Required',
                shipment.appointment_required && 'Appointment Required',
                shipment.inside_delivery && 'Inside Delivery',
                shipment.residential_delivery && 'Residential Delivery',
                shipment.tradeshow_delivery && 'Tradeshow Delivery',
                shipment.construction_site_delivery && 'Construction Site Delivery',
                shipment.notify_before_delivery && 'Notify Before Delivery',
                shipment.hold_at_terminal && 'Hold At Terminal',
                shipment.sort_and_segregate && 'Sort & Segregate',
                shipment.protect_from_freeze && 'Protect From Freeze',
              ].filter(Boolean) as string[]

              const canEditEmailPolicy = !['delivered', 'cancelled'].includes(shipment.status)
              // Booking inputs lead while we still book it; carrier numbers come from the booking (USA-1505).
              const toBook = isShipmentToBook(shipment)
              // A supplier's shipment not yet moving: what to record is its carrier and tracking.
              const recordSupplierShipment =
                shipment.booked_by === 'manufacturer' &&
                (shipment.status === 'pending' || shipment.status === 'ready_to_book')
              const expanded = openShipments.has(shipment.id)

              return (
                <Card key={shipment.id} data-testid={`po-shipment-${shipment.id}`} className="gap-3 py-4">
                  <CardHeader>
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                      <Link
                        to="/shipments/$shipmentId"
                        params={{ shipmentId: shipment.uuid }}
                        className="group flex min-w-0 items-center gap-2 rounded-md px-1 -ml-1 py-0.5 hover:bg-primary/5"
                      >
                        <Truck className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <CardTitle className="text-sm text-primary underline decoration-primary/40 underline-offset-4 group-hover:decoration-primary break-words">
                          Shipment
                          {shipment.ship_from_name && ` from ${shipment.ship_from_name}`}
                        </CardTitle>
                        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-primary/70 transition-transform group-hover:translate-x-0.5" />
                        <span className="sr-only">View shipment details</span>
                      </Link>
                      {/* Below sm: "Booked by" on its own line, then the controls in one row. */}
                      <div className="flex flex-wrap items-center gap-2 max-sm:w-full">
                        <ShipmentStatusEdit shipment={shipment} correct={correctShipmentStatus} />
                        <ShipmentModePicker po={po} shipment={shipment} />
                        {toBook || recordSupplierShipment ? (
                          <ShipmentBookerPicker po={po} shipment={shipment} />
                        ) : (
                          shipment.booked_by && (
                            <span className="text-xs text-muted-foreground">
                              {shipment.booked_by === 'us' ? 'We booked' : 'Supplier booked'}
                            </span>
                          )
                        )}
                        {canEditEmailPolicy && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setEditShipmentId(shipment.id)}
                          >
                            <Pencil className="mr-1 h-3 w-3" />
                            Email policy
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {!expanded && !toBook && !recordSupplierShipment && (
                      <ShipmentSummary
                        shipment={shipment}
                        onExpand={() => toggleShipment(shipment.id)}
                      />
                    )}

                    {/* Booking Error & Retry */}
                    {shipment.booking_error && (
                      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900 dark:bg-red-950/30">
                        <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                                Booking Failed
                              </p>
                            </div>
                            <p className="text-sm text-red-700 dark:text-red-300 break-words">
                              {shipment.booking_error}
                            </p>
                            {shipment.booking_failed_at && (
                              <p className="text-xs text-red-500 mt-1">
                                Failed at:{' '}
                                {parseUTCDate(shipment.booking_failed_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 sm:shrink-0 sm:flex-nowrap">
                            {shipment.status === 'ready_to_book' && shipment.booked_by === 'us' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950"
                                disabled={retryBookingMutation.isPending}
                                onClick={() => {
                                  retryBookingMutation.mutate(shipment.id, {
                                    onSuccess: () => toast.success('Booking retry queued'),
                                    onError: (err) =>
                                      toast.error(formatApiErrorMessage('Retry failed', err)),
                                  })
                                }}
                              >
                                <RefreshCw
                                  className={`mr-1.5 h-3.5 w-3.5 ${retryBookingMutation.isPending ? 'animate-spin' : ''}`}
                                />
                                Retry
                              </Button>
                            )}
                            {shipment.booked_by === 'us' && handBookingShipmentId !== shipment.id && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950"
                                onClick={() => setHandBookingShipmentId(shipment.id)}
                              >
                                <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" />
                                Booked by hand
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950"
                              onClick={() => setRebookShipmentId(shipment.id)}
                            >
                              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                              Rebook
                            </Button>
                          </div>
                        </div>
                        {handBookingShipmentId === shipment.id && (
                          <div className="mt-3 border-t border-red-200 pt-3 dark:border-red-900">
                            <HandBookingForm
                              poUuid={po.uuid}
                              shipmentId={shipment.id}
                              onDone={() => setHandBookingShipmentId(null)}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Rebook button for cancelled shipments without an active error banner */}
                    {!shipment.booking_error && shipment.status === 'cancelled' && (
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRebookShipmentId(shipment.id)}
                        >
                          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                          Rebook shipment
                        </Button>
                      </div>
                    )}

                    {toBook && (
                      <ShipmentBookingChecklist
                        po={po}
                        shipment={shipment}
                        bookShipmentMutation={bookShipmentMutation}
                      />
                    )}
                    {recordSupplierShipment && (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
                        <ShipmentReferenceFields shipment={shipment} correct={correctShipmentStatus} />
                      </div>
                    )}
                    {(toBook || recordSupplierShipment) && !expanded && (
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-muted-foreground"
                          onClick={() => toggleShipment(shipment.id)}
                          aria-expanded={false}
                          aria-label="Show shipment details"
                        >
                          <ChevronDown className="mr-1 h-3.5 w-3.5" />
                          Details
                        </Button>
                      </div>
                    )}

                    {expanded && (
                    <div className="space-y-5">
                    <div className="flex justify-end -mt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-muted-foreground"
                        onClick={() => toggleShipment(shipment.id)}
                        aria-expanded
                      >
                        <ChevronUp className="mr-1 h-3.5 w-3.5" />
                        Hide details
                      </Button>
                    </div>
                    {/* Dates grid */}
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <ShipmentDateCard
                        label="ASD (Actual Ship)"
                        date={shipment.actual_ship_date}
                        tone="asd"
                        icon={<CheckCircle2 className="h-4 w-4" />}
                      />
                      <ShipmentDateCard
                        label="ESD (Est. Ship)"
                        date={shipment.estimated_ship_date}
                        tone="esd"
                        icon={<CalendarArrowUp className="h-4 w-4" />}
                        strike={Boolean(shipment.actual_ship_date)}
                      />
                      <ShipmentDateCard
                        label="EDD (Est. Delivery)"
                        date={shipment.estimated_delivery_date}
                        tone="edd"
                        icon={<MapPin className="h-4 w-4" />}
                      />
                      {shipment.confirmed_ship_date && (
                        <div className="flex items-start gap-2 rounded-md border px-3 py-2">
                          <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              Confirmed Ship
                            </p>
                            <p className="text-sm font-medium">
                              {formatDateOnly(shipment.confirmed_ship_date)}
                            </p>
                          </div>
                        </div>
                      )}
                      {shipment.pickup_date && (
                        <div className="flex items-start gap-2 rounded-md border px-3 py-2">
                          <Truck className="mt-0.5 h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              Pickup
                            </p>
                            <p className="text-sm font-medium">
                              {formatDateOnly(shipment.pickup_date)}
                              {(shipment.pickup_time_start || shipment.pickup_time_end) && (
                                <span className="text-muted-foreground font-normal">
                                  {' '}
                                  {formatTime12h(shipment.pickup_time_start || '?')} -{' '}
                                  {formatTime12h(shipment.pickup_time_end || '?')}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <ShipmentHistory poUuid={po.uuid} shipmentId={shipment.id} />

                    {/* Ship From / Ship To addresses side by side */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      {!toBook &&
                        (shipment.ship_from_name ||
                        shipment.ship_from_address1 ||
                        shipment.ship_from_city) && (
                        <div className="rounded-md border px-4 py-3">
                          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
                            Ship From
                          </p>
                          <div className="text-sm space-y-0.5">
                            {shipment.ship_from_name && (
                              <p className="font-medium">{shipment.ship_from_name}</p>
                            )}
                            {shipment.ship_from_contact_name && (
                              <p className="text-muted-foreground">
                                {shipment.ship_from_contact_name}
                              </p>
                            )}
                            {shipment.ship_from_address1 && <p>{shipment.ship_from_address1}</p>}
                            {shipment.ship_from_address2 && <p>{shipment.ship_from_address2}</p>}
                            {(shipment.ship_from_city ||
                              shipment.ship_from_state ||
                              shipment.ship_from_zip) && (
                              <p>
                                {[shipment.ship_from_city, shipment.ship_from_state]
                                  .filter(Boolean)
                                  .join(', ')}
                                {shipment.ship_from_zip ? ` ${shipment.ship_from_zip}` : ''}
                              </p>
                            )}
                            {shipment.ship_from_country &&
                              shipment.ship_from_country !== 'US' &&
                              shipment.ship_from_country !== 'USA' && (
                                <p>{shipment.ship_from_country}</p>
                              )}
                            {shipment.ship_from_phone && (
                              <p className="text-muted-foreground">
                                Phone: {shipment.ship_from_phone}
                              </p>
                            )}
                            {shipment.ship_from_email && (
                              <p className="text-muted-foreground">{shipment.ship_from_email}</p>
                            )}
                          </div>
                        </div>
                      )}
                      {!toBook && (po.ship_to_name || po.ship_to_address1 || po.ship_to_city) && (
                        <div className="rounded-md border px-4 py-3">
                          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
                            Ship To
                          </p>
                          <div className="text-sm space-y-0.5">
                            {po.ship_to_name && <p className="font-medium">{po.ship_to_name}</p>}
                            {po.ship_to_company && <p>{po.ship_to_company}</p>}
                            {po.ship_to_address1 && <p>{po.ship_to_address1}</p>}
                            {po.ship_to_address2 && <p>{po.ship_to_address2}</p>}
                            {(po.ship_to_city || po.ship_to_state || po.ship_to_zip) && (
                              <p>
                                {[po.ship_to_city, po.ship_to_state].filter(Boolean).join(', ')}
                                {po.ship_to_zip ? ` ${po.ship_to_zip}` : ''}
                              </p>
                            )}
                            {po.ship_to_country &&
                              po.ship_to_country !== 'US' &&
                              po.ship_to_country !== 'USA' && <p>{po.ship_to_country}</p>}
                            {po.ship_to_phone && (
                              <p className="text-muted-foreground">Phone: {po.ship_to_phone}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Carrier, Tracking & Booking: the carrier facts are click to edit (USA-1466);
                        shown above for a supplier's shipment, and filled by booking for one we book. */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
                      {!toBook && !recordSupplierShipment && (
                        <ShipmentReferenceFields shipment={shipment} correct={correctShipmentStatus} />
                      )}
                      {shipment.booking_reference && (
                        <div>
                          <p className="text-xs text-muted-foreground">Booking Reference</p>
                          <p className="text-sm font-mono">{shipment.booking_reference}</p>
                        </div>
                      )}
                      {shipment.booked_at && (
                        <div>
                          <p className="text-xs text-muted-foreground">Booked At</p>
                          <p className="text-sm font-medium">
                            {parseUTCDate(shipment.booked_at).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {shipment.shipping_cost != null && (
                        <div>
                          <p className="text-xs text-muted-foreground">Shipping Cost</p>
                          <p className="text-sm font-bold">{formatMoney(shipment.shipping_cost)}</p>
                        </div>
                      )}
                    </div>

                    {/* Freight Summary */}
                    {!toBook &&
                      (shipment.pallet_count ||
                      shipment.total_weight_lbs ||
                      shipment.freight_class) && (
                      <div className="flex flex-wrap gap-4 rounded-md bg-muted/50 px-3 py-2 text-sm">
                        {shipment.pallet_count && (
                          <div className="flex items-center gap-1.5">
                            <Package className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>
                              {shipment.pallet_count} pallet{shipment.pallet_count > 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                        {shipment.total_weight_lbs && (
                          <span>{Number(shipment.total_weight_lbs).toLocaleString()} lbs</span>
                        )}
                        {shipment.freight_class && <span>Class {shipment.freight_class}</span>}
                      </div>
                    )}

                    {/* Handling Units (structured LTL cargo) */}
                    {!toBook && shipment.handling_units?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          Handling Units
                        </p>
                        <div className="space-y-2">
                          {shipment.handling_units.map((hu) => (
                            <div key={hu.id} className="rounded-md border px-3 py-2">
                              <div className="flex flex-wrap items-center gap-3 text-sm">
                                <span className="font-medium">
                                  {hu.num_units}x {hu.unit_type}
                                </span>
                                {(hu.length_in || hu.width_in || hu.height_in) && (
                                  <span className="text-muted-foreground">
                                    {[hu.length_in, hu.width_in, hu.height_in]
                                      .filter((v) => v != null)
                                      .join(' × ')}
                                    &quot;
                                  </span>
                                )}
                                {hu.stackable && (
                                  <Badge variant="outline" className="text-xs">
                                    Stackable
                                  </Badge>
                                )}
                              </div>
                              {hu.commodities?.length > 0 && (
                                <div className="mt-1.5 space-y-1 pl-3 border-l-2 border-muted">
                                  {hu.commodities.map((c) => (
                                    <div
                                      key={c.id}
                                      className="flex flex-wrap gap-3 text-xs text-muted-foreground"
                                    >
                                      <span>{c.description}</span>
                                      <span>{Number(c.weight_lbs).toLocaleString()} lbs</span>
                                      {c.pieces > 1 && <span>{c.pieces} pcs</span>}
                                      {c.freight_class && <span>Class {c.freight_class}</span>}
                                      {c.nmfc_code && <span>NMFC {c.nmfc_code}</span>}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Facts reported after booking, beside what we booked (USA-1697) */}
                    <ShipmentRevisions revisions={shipment.revisions} />

                    {/* Accessorials */}
                    {!toBook && (pickupAccessorials.length > 0 || deliveryAccessorials.length > 0) && (
                      <div className="space-y-2">
                        {pickupAccessorials.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              Pickup Requirements
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {pickupAccessorials.map((a) => (
                                <Badge key={a} variant="outline" className="text-xs">
                                  {a}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {deliveryAccessorials.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              Delivery Requirements
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {deliveryAccessorials.map((a) => (
                                <Badge key={a} variant="outline" className="text-xs">
                                  {a}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Shipping Notes */}
                    {!toBook && shipment.shipping_notes && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          Shipping Notes
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{shipment.shipping_notes}</p>
                      </div>
                    )}

                    {/* Documents: BOL & Label downloads */}
                    {(shipment.bol_pdf_filename || shipment.label_pdf_filename) && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1.5">
                          Documents
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {shipment.bol_pdf_filename && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                const token = await getAccessToken()
                                if (!token) return
                                const resp = await fetch(
                                  `${API_BASE_URL}/dashboard/shipments/${shipment.uuid}/bol`,
                                  {
                                    headers: { Authorization: `Bearer ${token}` },
                                  },
                                )
                                if (!resp.ok) return
                                const blob = await resp.blob()
                                const url = URL.createObjectURL(blob)
                                const a = document.createElement('a')
                                a.href = url
                                a.download = shipment.bol_pdf_filename ?? 'bol.pdf'
                                a.click()
                                URL.revokeObjectURL(url)
                              }}
                            >
                              <Download className="mr-1.5 h-3.5 w-3.5" />
                              BOL
                            </Button>
                          )}
                          {shipment.label_pdf_filename && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                const token = await getAccessToken()
                                if (!token) return
                                const resp = await fetch(
                                  `${API_BASE_URL}/dashboard/shipments/${shipment.uuid}/label`,
                                  {
                                    headers: { Authorization: `Bearer ${token}` },
                                  },
                                )
                                if (!resp.ok) return
                                const blob = await resp.blob()
                                const url = URL.createObjectURL(blob)
                                const a = document.createElement('a')
                                a.href = url
                                a.download = shipment.label_pdf_filename ?? 'label.pdf'
                                a.click()
                                URL.revokeObjectURL(url)
                              }}
                            >
                              <Download className="mr-1.5 h-3.5 w-3.5" />
                              Shipping Label
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Shipment Line Items */}
                    {shipment.line_items?.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                          Items in this shipment
                        </p>

                        {/* Desktop table */}
                        <div className="hidden md:block">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[52px]" />
                                <TableHead>Product</TableHead>
                                <TableHead className="text-right">Qty</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {shipment.line_items.map((sli) => (
                                <TableRow key={sli.id}>
                                  <TableCell className="pr-0">
                                    {sli.line_item?.product?.image_url ? (
                                      <img
                                        src={sli.line_item.product.image_url}
                                        alt=""
                                        className="h-10 w-10 rounded border object-cover"
                                      />
                                    ) : (
                                      <div className="flex h-10 w-10 items-center justify-center rounded border bg-muted">
                                        <Package className="h-4 w-4 text-muted-foreground" />
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell className="min-w-0 max-w-[520px] whitespace-normal">
                                    <ProductSummaryRow
                                      item={{
                                        id: sli.id,
                                        title: sli.line_item?.model,
                                        description: sli.line_item?.description,
                                        productUuid: sli.line_item?.product?.uuid,
                                        imageUrl: null,
                                        manufacturerName: sli.line_item?.product?.manufacturer_name,
                                        manufacturerUuid: sli.line_item?.product?.manufacturer_uuid,
                                      }}
                                      imageClassName="hidden"
                                    />
                                  </TableCell>
                                  <TableCell className="text-right">{sli.quantity}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Mobile cards */}
                        <div className="space-y-2 md:hidden">
                          {shipment.line_items.map((sli) => (
                            <div
                              key={sli.id}
                              className="flex items-start gap-3 rounded-lg border bg-card p-3"
                            >
                              <div className="shrink-0">
                                {sli.line_item?.product?.image_url ? (
                                  <img
                                    src={sli.line_item.product.image_url}
                                    alt=""
                                    className="h-10 w-10 rounded border object-cover"
                                  />
                                ) : (
                                  <div className="flex h-10 w-10 items-center justify-center rounded border bg-muted">
                                    <Package className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                {sli.line_item?.product?.uuid ? (
                                  <Link
                                    to="/products/$productId"
                                    params={{ productId: sli.line_item.product.uuid }}
                                    className="block break-words text-sm font-medium text-primary hover:underline"
                                  >
                                    {sli.line_item?.model || '-'}
                                  </Link>
                                ) : (
                                  <span className="block break-words text-sm font-medium">
                                    {sli.line_item?.model || '-'}
                                  </span>
                                )}
                                {sli.line_item?.product?.manufacturer_name && (
                                  <p className="text-xs text-muted-foreground">
                                    {sli.line_item.product.manufacturer_uuid ? (
                                      <Link
                                        to="/manufacturers/$manuId"
                                        params={{ manuId: sli.line_item.product.manufacturer_uuid }}
                                        className="hover:underline hover:text-primary"
                                      >
                                        {sli.line_item.product.manufacturer_name}
                                      </Link>
                                    ) : (
                                      sli.line_item.product.manufacturer_name
                                    )}
                                  </p>
                                )}
                              </div>
                              <div className="shrink-0 text-right">
                                <span className="text-xs text-muted-foreground">Qty</span>
                                <p className="text-sm font-semibold">{sli.quantity}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </div>
                    )}
                  </CardContent>
                </Card>
              )
            })
        : null}

      {(() => {
        const cancelledCount = (po.shipments ?? []).filter((s) => s.status === 'cancelled').length
        if (cancelledCount === 0) return null
        return (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => setShowCancelledShipments((v) => !v)}
            >
              {showCancelledShipments
                ? `Hide ${cancelledCount} cancelled ${cancelledCount === 1 ? 'shipment' : 'shipments'}`
                : `Show ${cancelledCount} cancelled ${cancelledCount === 1 ? 'shipment' : 'shipments'}`}
            </Button>
          </div>
        )
      })()}
    </>
  )
}
